import { Box, Card, Flex, Stack, Text, Title, useMantineColorScheme } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Modal } from "../Modal";
import { UserAvatar } from "../UserAvatar";
import MobileNavLink from "./MobileNavLink";
import MobileNavigationCards from "./MobileNavigationCards";
import { getUserMenuItems } from "./utils";

import { IconSportCar } from "@/components/icons";
import { APP_VERSION, global_settings } from "@/config";
import { paths } from "@/config/paths";
import { useLogout, useUser } from "@/services/auth";
import { useGarages } from "@/services/garages/get-garages";
import { useNotificationCounts } from "@/services/notifications/get-notification-count";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { useProfile } from "@/services/profile/get-profile";
import { getUserName } from "@/utils/getUserName";

import type { UserMenuItem } from "@/types/const";
import type { FC } from "react";

interface MobileNavigationProps {
  opened: boolean;
  close: () => void;
  openSupport: () => void;
  showThemeModal: () => void;
  openProfile: () => void;
  openBilling?: () => void;
  openNotifications?: () => void;
}

const MobileNavigation: FC<MobileNavigationProps> = ({
  close,
  opened,
  openSupport,
  showThemeModal,
  openProfile,
  openBilling,
  openNotifications,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const backgroundColor = isDark ? "#191919" : "#ffffff";

  /* API */
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const { data: profile } = useProfile();
  const { data: garages } = useGarages();
  const { data: plans } = usePlansUser();
  const { mutate: logoutMutate } = useLogout({
    onSuccess() {
      queryClient.removeQueries();
      navigate(paths.auth.login.getHref());
    },
  });

  const { data: notificationCounts } = useNotificationCounts();

  const notificationCount = notificationCounts?.delivered ?? 0;

  /* Functions */
  function logout() {
    logoutMutate({});
  }

  const zIndex = 100;

  /* Navigation */
  const garageChildren: UserMenuItem[] = [
    ...((garages?.results ?? []).map((garage) => ({
      label: garage.data.name,
      to: `/app/garages/${garage.identifier}/garage`,
      icon: <IconSportCar size={20} />,
      group: 1,
      hideIcons: garages && garages?.results.length > 0 ? true : false,
      numericIndicator: garage.data.belongs_to_car?.length ?? 0,
      colorIndicator: "primary",
    })) as UserMenuItem[]),
    {
      label: t("components.header.formerCars"),
      to: paths.app.formerCars.root.getHref(),
      // no icon for Former Cars, align with Garage children
      icon: null,
      hideIcons: true,
      group: 1,
    },
  ];

  const userMenuItems: UserMenuItem[] = getUserMenuItems(
    t,
    garageChildren,
    logout,
    openSupport,
    showThemeModal,
    openProfile,
    openNotifications,
    openBilling,
    plans,
    notificationCount ?? 0
  );

  const sanitizedUserMenuItems: UserMenuItem[] = userMenuItems.map((item) => ({
    ...item,
    children: item.children?.filter((child) => !child.hide),
  }));

  function isChild(item: UserMenuItem) {
    return sanitizedUserMenuItems.some((parent) => parent.children?.includes(item));
  }

  if (!user) return null;

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        fullScreen
        styles={{
          body: {
            paddingInline: "10px",
            backgroundColor,
          },
          content: {
            backgroundColor,
          },
          close: {
            color: "#143ab1",
            zIndex: zIndex,
          },
          header: {
            background: "transparent",
            zIndex: zIndex,
          },
        }}
      >
        <Flex direction="column" justify="space-between" h="100%">
          {/* Header */}
          <Box pt="lg">
            <Flex justify="center" m="0 auto" mb="md" id="prov">
              <Box style={{ zIndex: zIndex }}>
                <UserAvatar size={100} enableUpload={true} user={profile ?? user} />
              </Box>
            </Flex>

            <Title order={3} ta="center" mb="lg">
              {getUserName(profile ?? user)}
            </Title>

            {global_settings.enablePayments && <MobileNavigationCards closeMainModal={close} />}
          </Box>

          {/* Body */}
          <Box style={{ overflowY: "auto" }} h={0} flex={1}>
            <Stack gap="md" px="sm" py="lg">
              {[...new Set(sanitizedUserMenuItems.map((item) => item.group))]
                .sort()
                .map((groupId) => (
                  <Card
                    key={groupId}
                    p="lg"
                    style={{
                      backgroundColor,
                    }}
                  >
                    <Stack gap="md">
                      {sanitizedUserMenuItems
                        .filter((item) => item.group === groupId && !isChild(item))
                        .filter((item) => !item.hide)
                        .map((menuItem, index) => (
                          <MobileNavLink key={index} menuItem={menuItem} onNavigate={close} />
                        ))}
                    </Stack>
                  </Card>
                ))}
            </Stack>
          </Box>

          {/* Footer */}
          <Box mt="lg" pb="sm">
            <Text ta="center" c="gray" fw="normal">
              {t("common.version")}: {APP_VERSION}
            </Text>
          </Box>
        </Flex>
      </Modal>
    </>
  );
};

export default MobileNavigation;
